import {
  getCampaignPostsByCampaigns,
  getCampaignPostsByCategory,
} from "@/wp/getPosts.ts";
import { UserType } from "@/types/baseTypes.ts";
import { Header_Kampagne } from "@/wp/types/acf-header-types.ts";

export type CampaignWithId = Header_Kampagne["header_campaign"] & {
  id: string;
};

export async function getCampaignDataByCategoryIds(
  idsArray: string[],
  user: UserType | undefined | null,
  size: string,
  isRecipePage: boolean,
): Promise<CampaignWithId | null> {
  try {
    const campaigns = await getCampaignPostsByCategory(idsArray);
    if (!campaigns) return null;

    const filterForFormat = filterCampaignByFormat(campaigns, size);

    const matchesWithUsers = isRecipePage
      ? filterForFormat.filter((post) => filterByUser(user, post))
      : filterForFormat;

    const filteredByExactCategory = filterCampaignByExactCategory(
      matchesWithUsers,
      idsArray,
    );

    const randomCampaign = randomItemFromArray(
      filteredByExactCategory.length > 0
        ? filteredByExactCategory
        : matchesWithUsers,
    );
    return randomCampaign;
  } catch (error) {
    console.error("Error fetching campaignData by IDs: " + error);
  }
  return null;
}

export async function getCampaignDataByCampaignId(
  campaignIds: string[],
  size: string,
): Promise<CampaignWithId | null> {
  try {
    const campaigns = await getCampaignPostsByCampaigns();
    if (!campaigns) return null;
    const filterForFormat = filterCampaignByFormat(campaigns, size);
    const matchingData = filterForFormat.filter((campaign) => {
      return campaignIds.includes(String(campaign.id));
    });
    const randomCampaign = randomItemFromArray(matchingData);
    return randomCampaign;
  } catch (error) {
    console.error(error);
  }
  return null;
}

function filterCampaignByExactCategory(
  campaign: CampaignWithId[],
  idsArray: string[],
): CampaignWithId[] {
  return campaign.filter((el) => {
    if (!idsArray || idsArray.length === 0) return campaign;
    if (!el.categories || el.categories.length === 0) {
      return false;
    }

    return el.categories.some((categoryId) => {
      return idsArray.includes(categoryId);
    });
  });
}

function filterCampaignByFormat(
  campaignsArray: CampaignWithId[],
  format: string,
): CampaignWithId[] {
  const result = campaignsArray.filter((campaign) => {
    if (format === "video") {
      return campaign.media.video;
    }

    if (format === "big") {
      return (
        campaign.media.media_big.image_desktop &&
        campaign.media.media_big?.image_tablet &&
        campaign.media.media_big?.image_mobile
      );
    }
    if (format === "medium") {
      return (
        campaign.media.media_medium?.image_desktop &&
        campaign.media.media_medium?.image_tablet &&
        campaign.media.media_medium?.image_mobile
      );
    }
    if (format === "small") {
      return (
        campaign.media.media_small?.image_desktop &&
        campaign.media.media_small?.image_tablet &&
        campaign.media.media_small?.image_mobile
      );
    }
    return false;
  });
  return result;
}

function filterByUser(
  user: UserType | undefined | null,
  campaign: CampaignWithId,
): boolean {
  const userSelection = campaign.user_selection;
  const result = !!(
    (userSelection.includes("unregistered") && !user) ||
    (userSelection.includes("registered") && user)
  );
  return result;
}

function randomItemFromArray<T>(array: Array<T>) {
  const index = Math.floor(Math.random() * array.length);
  const result = array[index];
  return result;
}
